import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from '@chakra-ui/react';
import { ReactSVG } from 'react-svg';
import rightArrow from '../../../assets/images/icons/right-arrow.svg';
import { Drawer } from '../../../components/UI-CHAKRA/Drawer';
import { DrawerOverlay } from '../../../components/UI-CHAKRA/DrawerOverlay';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Input } from '../../../components/UI-CHAKRA/Input/Input';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { Checkbox } from '../../../components/UI-CHAKRA/Checkbox';
import { Switch } from '../../../components/UI-CHAKRA/Switch';
import { Tracker } from '../../../shared/tracker/Tracker';
import { TrackerEventService } from '../../prescription/trackerEventService';
import { ModelService } from '../services';
import { FormulaService } from '../../../components/search/formulaService';
import { buildCreateProtocolPayload } from './protocolMapping';
import { getMedicalCertificateLabel } from './medicalCertificateLabel';
import { amplitudeModule } from '../../../shared/amplitude';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { useExamStore } from '../../../stores/examStore';
import { activeModelSearchName, useModelStore } from '../../../stores/modelStore';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { useLoading } from '../../../helpers/useLoading';
import { Yup } from '../../../helpers/Yup';
import { toast } from '../../../helpers/toast';
import { extractPatientAge } from '../../../helpers/patientAge';
import { logger } from '../../../helpers/logger';
import { Formula } from '../../../interfaces/formula';

/**
 * Teto da busca na biblioteca de fórmulas ao resolver o id para o modelo. O endpoint casa por
 * termo parcial, então um nome curto traz muitos vizinhos e o homônimo exato poderia ficar
 * fora da primeira página.
 */
const FORMULA_LOOKUP_LIMIT = 50;

const normalizeFormulaName = (value: string) => value.trim().toLowerCase();

export interface FormulaResolution {
  formula: Formula;
  resolved: Formula | null;
  name: string;
}

/**
 * Troca o id do item da prescrição pelo id da fórmula na biblioteca do médico, que é o que o
 * contrato de protocolo referencia. A busca casa por termo parcial, então só aceitamos um
 * homônimo exato e único — cair no primeiro resultado gravaria a fórmula errada no modelo.
 *
 * @param doctorId prescritor dono da biblioteca
 * @param formulas fórmulas selecionadas na prescrição
 * @returns uma entrada por fórmula, com `resolved` nulo quando não houve match único
 */
export async function resolveLibraryFormulas(doctorId: number, formulas: Formula[]): Promise<FormulaResolution[]> {
  return Promise.all(
    formulas.map(async (formula) => {
      const name = formula.formulaName || (formula as unknown as { NomeFormula?: string }).NomeFormula || '';
      if (!name) return { formula, resolved: null, name: 'Fórmula sem nome' };
      try {
        const { formulas: library } = await FormulaService.searchFormula(doctorId, name, 1, FORMULA_LOOKUP_LIMIT);
        const matches = (library ?? []).filter(
          (libraryFormula) => normalizeFormulaName(libraryFormula.formulaName) === normalizeFormulaName(name)
        );
        if (matches.length !== 1) {
          logger.warn('NewModelDrawer: library formula not uniquely resolved', { name, matches: matches.length });
          return { formula, resolved: null, name };
        }
        return { formula, resolved: { ...formula, id: matches[0].id }, name };
      } catch (e) {
        logger.error('NewModelDrawer: failed resolving library formula id', { name, error: e });
        return { formula, resolved: null, name };
      }
    })
  );
}

const NewModelSchema = Yup.object().shape({
  modelName: Yup.string().trim().required('Informe um nome para o modelo'),
});

interface NewModelDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type SelectionType =
  | 'medicine'
  | 'manualMedicine'
  | 'exam'
  | 'formula'
  | 'certificate'
  | 'referral'
  | 'report';

type SelectionState = Record<SelectionType, Set<number>>;

const EMPTY_SELECTION: SelectionState = {
  medicine: new Set<number>(),
  manualMedicine: new Set<number>(),
  exam: new Set<number>(),
  formula: new Set<number>(),
  certificate: new Set<number>(),
  referral: new Set<number>(),
  report: new Set<number>(),
};

interface ItemPreview {
  key: string;
  primary: string;
  secondary?: string;
  kind: SelectionType;
  index: number;
}

function buildPosology(item: { Posologia?: string | null; QuantidadeUnidadeFormatada?: string | null }): string {
  const parts = [item.Posologia, item.QuantidadeUnidadeFormatada].filter(Boolean) as string[];
  return parts.join(' · ');
}

/**
 * Indica se a falha do `POST /protocols` foi por tempo esgotado, e não uma recusa.
 *
 * O BFF responde 504 quando desiste de esperar o protocols-service, que segue processando e
 * pode gravar o modelo logo depois. Nesse caso o resultado é desconhecido, e a mensagem não
 * pode afirmar que o modelo não foi salvo.
 *
 * @param error Erro lançado pelo `ModelService.createModel`.
 * @returns `true` para 504 do BFF ou timeout do próprio cliente HTTP.
 */
function isCreateTimeout(error: unknown): boolean {
  const { code, response } = (error ?? {}) as { code?: string; response?: { status?: number } };
  return response?.status === 504 || code === 'ECONNABORTED';
}

export function NewModelDrawer(props: NewModelDrawerProps) {
  const { isOpen, onClose } = props;

  const prescription = usePrescriptionStore((store) => store.prescription);
  const prescriber = usePrescriptionStore((store) => store.prescriber());
  const isInstitutionalModelAdmin = useModelStore((store) => store.isInstitutionalModelAdmin);
  const [isInstitutional, setIsInstitutional] = useState(false);
  const { fetchModels } = useModelStore();
  const setFeedback = useModelStore((store) => store.setFeedback);
  const [loading, runWithLoading] = useLoading();
  const isMobile = useIsMobile();

  const medicineItems: ItemPreview[] = useMemo(() => {
    if (!prescription) return [];
    const fromCatalog = (prescription.Itens ?? []).map((item, index) => ({
      key: `med-${item.idPrescricaoMedicamento ?? item.IdMedicamento}`,
      primary: item.Label || item.Nome || '',
      secondary: buildPosology(item),
      kind: 'medicine' as const,
      index,
    }));
    const manual = (prescription.ItensManuais ?? []).map((item, index) => ({
      key: `manual-${item.idPrescricaoMedicamentoManual}`,
      primary: item.Label || item.Nome || '',
      secondary: buildPosology(item),
      kind: 'manualMedicine' as const,
      index,
    }));
    return [...fromCatalog, ...manual];
  }, [prescription]);

  const examItems: ItemPreview[] = useMemo(() => {
    if (!prescription) return [];
    return (prescription.Exames ?? []).map((item, index) => ({
      key: `exam-${item.idPrescricaoExame ?? item.examId ?? item.Nome}`,
      primary: item.Nome ?? '',
      kind: 'exam' as const,
      index,
    }));
  }, [prescription]);

  const formulaItems: ItemPreview[] = useMemo(() => {
    if (!prescription) return [];
    return (prescription.Formulas ?? []).map((item, index) => {
      const summary = item as unknown as { NomeFormula?: string; Posologia?: string };
      return {
        key: `formula-${item.id}`,
        primary: item.formulaName || summary.NomeFormula || '',
        secondary: item.posology || summary.Posologia || '',
        kind: 'formula' as const,
        index,
      };
    });
  }, [prescription]);

  const certificateItems: ItemPreview[] = useMemo(() => {
    if (!prescription) return [];
    return (prescription.Atestados ?? []).map((item, index) => ({
      key: `cert-${item.idPrescricaoAtestado}`,
      primary: getMedicalCertificateLabel(item.Tipo, prescriber?.Conselho),
      kind: 'certificate' as const,
      index,
    }));
  }, [prescription, prescriber?.Conselho]);

  const referralItems: ItemPreview[] = useMemo(() => {
    if (!prescription) return [];
    return (prescription.Encaminhamentos ?? []).map((item, index) => ({
      key: `ref-${item.idEncaminhamentoPrescricao}`,
      primary: item.Especialidade?.Nome ?? 'Encaminhamento',
      kind: 'referral' as const,
      index,
    }));
  }, [prescription]);

  const reportItems: ItemPreview[] = useMemo(() => {
    if (!prescription) return [];
    return (prescription.Relatorios ?? []).map((item, index) => ({
      key: `report-${item.idPrescricaoLaudoMedico}`,
      primary: item.Titulo || 'Relatório',
      kind: 'report' as const,
      index,
    }));
  }, [prescription]);

  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const prescriptionObservation = prescription?.Comentario?.trim() ? prescription.Comentario : null;
  const [includeObservation, setIncludeObservation] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIncludeObservation(false);
      setSelection(EMPTY_SELECTION);
      setIsInstitutional(false);
      return;
    }
    setIsInstitutional(false);
    const current = usePrescriptionStore.getState().prescription;
    const allIndexes = (list?: unknown[]) => new Set((list ?? []).map((_, idx) => idx));
    setSelection({
      medicine: allIndexes(current?.Itens),
      manualMedicine: allIndexes(current?.ItensManuais),
      exam: allIndexes(current?.Exames),
      formula: allIndexes(current?.Formulas),
      certificate: allIndexes(current?.Atestados),
      referral: allIndexes(current?.Encaminhamentos),
      report: allIndexes(current?.Relatorios),
    });
  }, [isOpen]);

  const toggle = (type: SelectionType, idx: number) => {
    setSelection((prev) => {
      const set = new Set(prev[type]);
      if (set.has(idx)) set.delete(idx);
      else set.add(idx);
      return { ...prev, [type]: set };
    });
  };

  const hasAnyItem =
    Boolean(prescriptionObservation) ||
    medicineItems.length > 0 ||
    examItems.length > 0 ||
    formulaItems.length > 0 ||
    certificateItems.length > 0 ||
    referralItems.length > 0 ||
    reportItems.length > 0;

  const hasAnySelected =
    includeObservation ||
    selection.medicine.size > 0 ||
    selection.manualMedicine.size > 0 ||
    selection.exam.size > 0 ||
    selection.formula.size > 0 ||
    selection.certificate.size > 0 ||
    selection.referral.size > 0 ||
    selection.report.size > 0;

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      modelName: '',
    },
    validationSchema: NewModelSchema,
    onSubmit: (values) => {
      if (!prescription) return;

      runWithLoading(async () => {
        try {
          const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;
          const partnerId = prescriberConfiguration?.idParceiro;

          if (!partnerId) {
            toast.error('Configuração do parceiro não encontrada. Recarregue a página e tente novamente.');
            return;
          }

          const doctorId = prescription.Prescritor.idPrescritor;
          const saveAsInstitutional = isInstitutionalModelAdmin && isInstitutional;

          const duplicated = await ModelService.findModelByName(
            doctorId,
            prescription.idPrescricao,
            partnerId,
            values.modelName,
            saveAsInstitutional ? 'institutional' : 'personal',
          );
          if (duplicated) {
            formik.setFieldError('modelName', 'Não é possível salvar modelos com o mesmo nome');
            return;
          }
          const selectedFormulas = (prescription.Formulas ?? []).filter((_, idx) => selection.formula.has(idx));
          const resolutions = await resolveLibraryFormulas(doctorId, selectedFormulas);

          const resolvedFormulas = resolutions
            .map((resolution) => resolution.resolved)
            .filter(Boolean) as typeof selectedFormulas;
          const unresolvedFormulas = resolutions.filter((resolution) => !resolution.resolved).map((r) => r.name);

          const selectedItems = {
            medicines: (prescription.Itens ?? []).filter((_, idx) => selection.medicine.has(idx)),
            manualMedicines: (prescription.ItensManuais ?? []).filter((_, idx) => selection.manualMedicine.has(idx)),
            exams: (prescription.Exames ?? []).filter((_, idx) => selection.exam.has(idx)),
            certificates: (prescription.Atestados ?? []).filter((_, idx) => selection.certificate.has(idx)),
            referrals: (prescription.Encaminhamentos ?? []).filter((_, idx) => selection.referral.has(idx)),
            reports: (prescription.Relatorios ?? []).filter((_, idx) => selection.report.has(idx)),
            formulas: resolvedFormulas,
          };

          const payload = buildCreateProtocolPayload({
            name: values.modelName.trim(),
            doctorId,
            partnerId,
            prescriptionId: prescription.idPrescricao,
            ...(saveAsInstitutional && { origin: 'institutional' as const }),
            ...(includeObservation && prescriptionObservation ? { observation: prescriptionObservation } : {}),
            examGeneralObservation: useExamStore.getState().generalExamObservation,
            items: selectedItems,
          });
          const refreshModels = () =>
            fetchModels(
              prescription.Prescritor.idPrescritor,
              prescription.idPrescricao,
              extractPatientAge(prescription.Paciente?.Idade),
              activeModelSearchName(),
              { force: true },
            );

          let data: Awaited<ReturnType<typeof ModelService.createModel>>;
          try {
            data = await ModelService.createModel(payload);
          } catch (e) {
            logger.error('Error creating model:', e);
            // Timeout no BFF não quer dizer que o protocols-service desistiu: o modelo pode
            // ter sido gravado depois da resposta de erro. Recarregar a lista faz ele aparecer,
            // em vez de o médico só descobrir pela checagem de nome repetido na tentativa seguinte.
            await refreshModels();
            toast.error(
              isCreateTimeout(e)
                ? 'Não foi possível confirmar o salvamento do modelo. Confira a lista antes de tentar novamente.'
                : 'Não foi possível salvar o modelo, tente novamente.'
            );
            return;
          }

          try {
            Tracker.trackEvent({
              event: 'Model Saved',
              parameters: {
                savedModel: values.modelName,
              },
            });
          } catch (e) {
            logger.error('NewModelDrawer: failed tracking Model Saved', e);
          }

          try {
            const createdAt = new Date().toISOString();
            amplitudeModule.protocolCreated({
              screen_name: 'criação de modelo',
              flow: 'criação de modelo',
              partner_id: partnerId,
              partner_name: prescriberConfiguration?.Nome ?? '',
              establishment_name: prescription.Estabelecimento?.Nome ?? '',
              protocol: [
                {
                  id: data.id,
                  name: data.name,
                  is_sponsored: false,
                  is_institutional: saveAsInstitutional,
                  created_at: createdAt,
                  updated_at: createdAt,
                  is_edited: false,
                  medications: selectedItems.medicines
                    .map((medicine) => ({
                      id: String(medicine.IdMedicamento),
                      name: medicine.Nome ?? medicine.Label ?? '',
                      is_sponsored: medicine.isPremiumPlacement ?? false,
                    }))
                    .concat(
                      selectedItems.manualMedicines.map((medicine) => ({
                        id: '',
                        name: medicine.Nome ?? medicine.Label ?? '',
                        is_sponsored: false,
                      }))
                    ),
                  medical_test: selectedItems.exams.map((exam) => ({
                    id: exam.CodigoTUSS ?? exam.idEzOrder?.toString() ?? '',
                    name: exam.Nome ?? exam.Label ?? '',
                  })),
                  doctors_note: selectedItems.certificates.map((certificate) => ({
                    id: String(certificate.idPrescricaoAtestado),
                    name: getMedicalCertificateLabel(certificate.Tipo, prescriber?.Conselho),
                  })),
                  referral: selectedItems.referrals.map((referral) => ({
                    id: String(referral.idEncaminhamentoPrescricao),
                    name: referral.Especialidade?.Nome ?? '',
                  })),
                  medical_report: selectedItems.reports.map((report) => ({
                    id: String(report.idPrescricaoLaudoMedico),
                    name: report.Titulo ?? '',
                  })),
                  compounded_medications: resolvedFormulas.map((formula) => ({
                    id: String(formula.id),
                    name: formula.formulaName ?? '',
                  })),
                },
              ],
            });
          } catch (e) {
            logger.error('NewModelDrawer: failed tracking protocol_created', e);
          }

          const premiumMedicineIds = (prescription.Itens ?? [])
            .filter((medicine) => medicine.isPremiumPlacement)
            .map((item) => item.IdMedicamento);

          if (premiumMedicineIds.length > 0) {
            try {
              await TrackerEventService.track(prescription.idPrescricao, 'Modelo_criado_com_item_patrocinado', {
                idPrescricao: prescription.idPrescricao,
                idPrescricaoModelo: data.id,
                idMedico: prescription.Prescritor.idPrescritor,
                idMedicamento: premiumMedicineIds,
              });
            } catch {
            }
          }

          await refreshModels();

          setFeedback({
            type: 'saved',
            modelName: values.modelName.trim(),
            unsavedFormulas: unresolvedFormulas.length ? unresolvedFormulas : undefined,
          });
          formik.resetForm();
          onClose();
        } catch (e) {
          logger.error('Error creating model:', e);
          toast.error('Não foi possível salvar o modelo, tente novamente.');
        }
      });
    },
  });

  const handleClose = () => {
    if (loading) return;
    formik.resetForm();
    onClose();
  };

  const isInvalid = Boolean(formik.errors.modelName) && formik.touched.modelName;

  return (
    <Drawer isOpen={isOpen} placement={'right'} onClose={handleClose} size={'md'}>
      <DrawerOverlay bg={'rgba(0, 0, 0, 0.4)'} />
      <DrawerContent maxW={{ base: '100%', md: '445px' }} data-cy={'novo-modelo-drawer'}>
        <DrawerHeader
          display={'flex'}
          alignItems={'center'}
          h={'72px'}
          px={'24px'}
          py={'24px'}
          fontSize={'18px'}
          fontWeight={600}
          color={'#1E252B'}
          borderBottom={'1px solid #DEE2E6'}
        >
          Novo modelo
        </DrawerHeader>
        <DrawerCloseButton top={'24px'} right={'24px'} color={'#1E252B'} />

        <DrawerBody p={0} overflowY={'auto'}>
          <form id={'novo-modelo-form'} onSubmit={formik.handleSubmit}>
            <Box px={'16px'} py={'16px'} borderBottom={'1px solid #DEE2E6'}>
              <Input
                placeholder={'Digite o nome do modelo'}
                value={formik.values.modelName}
                onChange={(e) => formik.setFieldValue('modelName', e.target.value)}
                onBlur={() => formik.setFieldTouched('modelName', true)}
                maxLength={30}
                h={'40px'}
                fontSize={'14px'}
                borderRadius={'10px'}
                borderColor={isInvalid ? 'error.main' : '#CBBFE6'}
                data-testid={'novo-modelo-name-input'}
                autoFocus
              />
              {isInvalid && (
                <Text mt={'6px'} fontSize={'12px'} color={'error.main'}>
                  {formik.errors.modelName}
                </Text>
              )}
              {isInstitutionalModelAdmin && (
                <Switch
                  id={'novo-modelo-institucional'}
                  mt={'16px'}
                  display={'flex'}
                  alignItems={'center'}
                  isChecked={isInstitutional}
                  onChange={(e) => setIsInstitutional(e.target.checked)}
                  data-testid={'novo-modelo-institucional'}
                >
                  <Text fontSize={'14px'} lineHeight={'1.2'} color={'#343A40'}>
                    Tornar este modelo institucional
                  </Text>
                </Switch>
              )}
            </Box>

            {prescriptionObservation && (
              <Section title={'Observação'}>
                <ItemRow
                  primary={prescriptionObservation}
                  multiline
                  isChecked={includeObservation}
                  onToggle={() => setIncludeObservation((previous) => !previous)}
                />
              </Section>
            )}

            {medicineItems.length > 0 && (
              <Section title={'Medicamento'}>
                {medicineItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    primary={item.primary}
                    secondary={item.secondary}
                    isChecked={selection[item.kind].has(item.index)}
                    onToggle={() => toggle(item.kind, item.index)}
                  />
                ))}
              </Section>
            )}

            {formulaItems.length > 0 && (
              <Section title={'Fórmula'}>
                {formulaItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    primary={item.primary}
                    secondary={item.secondary}
                    isChecked={selection[item.kind].has(item.index)}
                    onToggle={() => toggle(item.kind, item.index)}
                  />
                ))}
              </Section>
            )}

            {examItems.length > 0 && (
              <Section title={'Exames e Cirurgias'}>
                {examItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    primary={item.primary}
                    secondary={item.secondary}
                    isChecked={selection[item.kind].has(item.index)}
                    onToggle={() => toggle(item.kind, item.index)}
                  />
                ))}
              </Section>
            )}

            {certificateItems.length > 0 && (
              <Section title={'Atestado'}>
                {certificateItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    primary={item.primary}
                    secondary={item.secondary}
                    isChecked={selection[item.kind].has(item.index)}
                    onToggle={() => toggle(item.kind, item.index)}
                  />
                ))}
              </Section>
            )}

            {referralItems.length > 0 && (
              <Section title={'Encaminhamento'}>
                {referralItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    primary={item.primary}
                    secondary={item.secondary}
                    isChecked={selection[item.kind].has(item.index)}
                    onToggle={() => toggle(item.kind, item.index)}
                  />
                ))}
              </Section>
            )}

            {reportItems.length > 0 && (
              <Section title={'Relatório'}>
                {reportItems.map((item) => (
                  <ItemRow
                    key={item.key}
                    primary={item.primary}
                    secondary={item.secondary}
                    isChecked={selection[item.kind].has(item.index)}
                    onToggle={() => toggle(item.kind, item.index)}
                  />
                ))}
              </Section>
            )}

            {!hasAnyItem && (
              <Box px={'24px'} py={'24px'}>
                <Text fontSize={'14px'} color={'#868E96'}>
                  Adicione itens à prescrição para criar um modelo.
                </Text>
              </Box>
            )}
          </form>
        </DrawerBody>

        <DrawerFooter
          borderTop={'1px solid #DEE2E6'}
          gap={{ base: '12px', md: '16px' }}
          justifyContent={{ base: 'space-between', md: 'flex-end' }}
          py={'20px'}
        >
          <Button
            variant={'outline'}
            borderRadius={'20px'}
            borderColor={'primary.main'}
            color={'primary.main'}
            minW={'100px'}
            flex={{ base: 1, md: 'unset' }}
            h={'40px'}
            onClick={handleClose}
            isDisabled={loading}
          >
            {isMobile ? 'Voltar' : 'Cancelar'}
          </Button>
          <Button
            type={'submit'}
            form={'novo-modelo-form'}
            variant={'solid'}
            borderRadius={'20px'}
            minW={'120px'}
            flex={{ base: 1, md: 'unset' }}
            h={'40px'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={'8px'}
            isLoading={loading}
            isDisabled={!hasAnyItem || !hasAnySelected}
            data-testid={'novo-modelo-submit'}
            rightIcon={isMobile ? <ReactSVG src={rightArrow} /> : undefined}
          >
            Salvar
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Box px={'24px'} py={'8px'}>
        <Text fontSize={'14px'} fontWeight={600} color={'#1E252B'}>
          {props.title}
        </Text>
      </Box>
      {props.children}
    </Box>
  );
}

interface ItemRowProps {
  primary: string;
  secondary?: string;
  isChecked?: boolean;
  multiline?: boolean;
  isReadOnly?: boolean;
  onToggle?: () => void;
}

function ItemRow(props: ItemRowProps) {
  const { primary, secondary, isChecked = true, multiline, isReadOnly = false, onToggle } = props;
  const primaryColor = usePrescriptionStore((store) => store.primaryColor());
  const checkboxSx = {
    '.chakra-checkbox__control[data-checked]': {
      background: primaryColor,
      borderColor: primaryColor,
      color: 'white',
    },
    '.chakra-checkbox__control[data-checked]:hover': {
      background: primaryColor,
      borderColor: primaryColor,
    },
  };

  return (
    <Flex
      alignItems={'flex-start'}
      gap={'12px'}
      px={'24px'}
      py={'12px'}
      borderTop={'1px solid #F1F3F5'}
    >
      <Box pt={'2px'}>
        <Checkbox
          isChecked={isChecked}
          isReadOnly={isReadOnly}
          onChange={isReadOnly ? undefined : onToggle}
          sx={checkboxSx}
        />
      </Box>
      <Flex direction={'column'} gap={'4px'} minW={0}>
        <Text
          fontSize={'14px'}
          fontWeight={500}
          color={'#1E252B'}
          lineHeight={'1.3'}
          whiteSpace={multiline ? 'pre-wrap' : 'normal'}
          wordBreak={'break-word'}
        >
          {primary}
        </Text>
        {secondary && (
          <Text fontSize={'12px'} color={'#868E96'} lineHeight={'1.3'}>
            {secondary}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
